<script lang="ts" setup>
import { useRoute, useRouter } from "vue-router";
import { computed, ref, watch, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import {
  Breadcrumbs,
  DraggableGrid,
  FlashMessage,
  LoadingSpinner,
  Dropdown,
} from "@/components";
import DeleteCustomFlowModal from "./DeleteCustomFlowModal.vue";
import DocumentAndPagesPicker from "./DocumentAndPagesPicker.vue";
import RenameCustomFlowModal from "./RenameCustomFlowModal.vue";
import DuplicateCustomFlowModal from "./DuplicateCustomFlowModal.vue";
import DeleteFlowPageModal from "./DeleteFlowPageModal.vue";
import ShareCustomFlowModal from "./share_flow_modal/ShareCustomFlowModal.vue";
import CustomFlowPage from "./CustomFlowPage.vue";
import {
  useCustomFlowQuery,
  useFlowIsSharedQuery,
  useAppendFlowItemsMutation,
  useReorderFlowPageMutation,
  useAppendVideoMutation,
  type FlowItem,
} from "@/graphql/operations";
import { userData } from "@/init";

let active = true;
onBeforeUnmount(() => {
  active = false;
  clearTimeout(flashMessage.timer);
});

const route = useRoute();
const router = useRouter();

const flowId = computed(() => route.params.id as string);
const canUseSharedSpaces = computed(
  () => userData.value?.canUseSharedSpaces() ?? true,
);

const { t } = useI18n({});

const {
  data,
  fetching: flowFetching,
  executeQuery,
} = useCustomFlowQuery({
  variables: computed(() => ({
    id: flowId.value,
  })),
  requestPolicy: "cache-and-network",
});

const currentUserId = computed(
  () => userData.value?.postgresId?.toString() ?? null,
);
const rawFlow = computed(() => data.value?.flow);
const flow = computed(() =>
  rawFlow.value?.creatorId?.toString() === currentUserId.value
    ? rawFlow.value
    : null,
);
const pages = computed(() => flow.value?.flowItems.nodes);

const { data: sharedData } = useFlowIsSharedQuery({
  variables: computed(() => ({ flowId: flowId.value })),
  pause: computed(() => !canUseSharedSpaces.value),
});
const isFlowShared = computed(
  () => (sharedData.value?.documentSpaceItems?.totalCount ?? 0) > 0,
);

const legalAppendixPages = computed(() => flow.value?.legalAppendixPages.nodes);
const hasLegalAppendixPages = computed(
  () => legalAppendixPages.value && legalAppendixPages.value.length > 0,
);

/**
 * Breadcrumbs
 */
const breadcrumbItems = computed(() => [
  {
    text: "Custom Flows",
    href: router.resolve({ name: "CustomFlows" }).fullPath,
  },
  {
    text: flow.value?.title,
  },
]);

/**
 * Flash Message
 */
const flashMessage = {
  isActive: ref<boolean>(false),
  state: ref<string>(""),
  message: ref<string>(""),
  spaceId: ref<string | undefined | null>(""),
  timer: 0,
  showRouter: false,
  show: (
    message: string,
    state: string,
    spaceId?: string,
    showRouter: boolean = false,
  ) => {
    if (!active) return;
    clearTimeout(flashMessage.timer);
    flashMessage.isActive.value = true;
    flashMessage.message.value = message;
    flashMessage.state.value = state;
    flashMessage.spaceId.value = spaceId;
    flashMessage.showRouter = showRouter;

    flashMessage.timer = window.setTimeout(() => {
      flashMessage.isActive.value = false;
      flashMessage.message.value = "";
    }, 3000);
  },
};

/**
 * Delete Flow
 */
const showDeleteModal = ref<boolean>(false);
const displayDeleteConfirmation = () => {
  showDeleteModal.value = true;
};

// The shared dialog owns the mutation; navigate only after its success event.
const deleteFlow = () => {
  if (!active) return;
  router.push({ name: "CustomFlows" });
  showDeleteModal.value = false;
};

/**
 * Add Pages
 */
const appendingPages = ref(false);
const isDocumentPagePickerActive = ref<boolean>(false);
const openDocumentPagePicker = () => {
  isDocumentPagePickerActive.value = true;
};

const closeDocumentPagePicker = () => {
  isDocumentPagePickerActive.value = false;
};

const { executeMutation: addFlowItems } = useAppendFlowItemsMutation();
const appendFlowPages = async (pageIds: Array<string>) => {
  if (appendingPages.value || pageIds.length === 0) return;
  appendingPages.value = true;
  try {
    const res = await addFlowItems({
      flowId: flowId.value,
      pageIds: pageIds,
    });
    if (res.error) {
      throw new Error(res.error.message);
    }
    closeDocumentPagePicker();
    await executeQuery();
    flashMessage.show(
      t("customFlows.flashMessages.addingPages.success"),
      "success",
    );
  } catch {
    flashMessage.show(
      t("customFlows.flashMessages.addingPages.error"),
      "error",
    );
  } finally {
    appendingPages.value = false;
  }
};

const { executeMutation: addVideoItem } = useAppendVideoMutation();
const addVideo = async (videoId: string) => {
  if (appendingPages.value) return;
  appendingPages.value = true;
  try {
    const res = await addVideoItem({ flowId: flowId.value, videoId });
    if (res.error) throw res.error;
    closeDocumentPagePicker();
    await executeQuery();
    flashMessage.show(
      t("customFlows.flashMessages.addingPages.success"),
      "success",
    );
  } catch {
    flashMessage.show(
      t("customFlows.flashMessages.addingPages.error"),
      "error",
    );
  } finally {
    appendingPages.value = false;
  }
};

/**
 * Delete Flow Pages
 */
const showRemovePageModal = ref<boolean>(false);
const flowPageIdToBeRemoved = ref<string | null>(null);

const handleRemoveFlowPage = (id: string) => {
  flowPageIdToBeRemoved.value = id;
  showRemovePageModal.value = true;
};

const handlePageDeleted = async () => {
  showRemovePageModal.value = false;
  await executeQuery();
  flashMessage.show(
    t("customFlows.flashMessages.deletingPage.success"),
    "success",
  );
};
const handleErrorDeletingPage = () => {
  showRemovePageModal.value = false;
  flashMessage.show(t("customFlows.flashMessages.deletingPage.error"), "error");
};
const closeRemovePageModal = () => {
  showRemovePageModal.value = false;
};

/**
 * Presenter
 */
const viewPageInPresenter = (pageNumber: number = 1) => {
  router.push({
    name: "ViewCustomFlowPageByPage",
    params: { id: flowId.value },
    query: { page: pageNumber },
  });
};

/**
 * Drag and Drop
 */
const { executeMutation } = useReorderFlowPageMutation();
async function placeBefore(flowItemId: string, beforeId: string) {
  await executeMutation({
    flowItemId: flowItemId,
    beforeId: beforeId,
  });
}

async function placeBehind(flowItemId: string, behindId: string) {
  await executeMutation({
    flowItemId: flowItemId,
    behindId: behindId,
  });
}

/**
 * Rename Flow
 */
const isRenameModalActive = ref<boolean>(false);
const openRenameModal = () => {
  isRenameModalActive.value = true;
};
const closeRenameModal = () => {
  isRenameModalActive.value = false;
};
const handleFlowRenamed = () => {
  executeQuery();
  flashMessage.show(
    t("customFlows.flashMessages.renamingFlow.success"),
    "success",
  );
  closeRenameModal();
};

/**
 * Duplicate Flow
 */
const isDuplicateModalActive = ref<boolean>(false);
const openDuplicateModal = () => {
  isDuplicateModalActive.value = true;
};
const closeDuplicateModal = () => {
  isDuplicateModalActive.value = false;
};
const handleFlowDuplicated = (newFlowId: string) => {
  closeDuplicateModal();
  flashMessage.show(t("customFlows.duplicateSuccess"), "success");
  router.push({ name: "CustomFlow", params: { id: newFlowId } });
};
const handleDuplicateError = () => {
  closeDuplicateModal();
  flashMessage.show(t("customFlows.duplicateError"), "error");
};

/**
 * Share Flow to Shared Space
 */
const isShareModalActive = ref<boolean>(false);
const openShareModal = () => {
  if (!canUseSharedSpaces.value) return;
  isShareModalActive.value = true;
};
const closeShareModal = () => {
  isShareModalActive.value = false;
};
watch(canUseSharedSpaces, (enabled) => {
  if (!enabled) closeShareModal();
});
const handleShareFlow = (
  resultState: string,
  spaceIds: Array<string>,
  message?: string,
) => {
  flashMessage.show(
    message ??
      (resultState === "success"
        ? t("customFlows.flashMessages.share.success")
        : t("customFlows.flashMessages.share.error")),
    resultState,
  );
  closeShareModal();
};

const getDocumentTitle = (page: FlowItem | undefined) => {
  if (!page) {
    return null;
  }

  return page.page
    ? page!.page?.document?.displayTitle
    : page!.video?.displayTitle;
};

const actionItems = [
  {
    value: "append_pages",
    title: t("customFlows.detail.addPages"),
  },
  {
    value: "rename",
    title: t("customFlows.detail.rename"),
  },
  {
    value: "duplicate",
    title: t("customFlows.detail.duplicate"),
  },
  {
    value: "delete",
    title: t("customFlows.detail.delete"),
    destructive: true,
  },
];

const handleAction = (action: string) => {
  switch (action) {
    case "append_pages":
      openDocumentPagePicker();
      break;

    case "rename":
      openRenameModal();
      break;

    case "duplicate":
      openDuplicateModal();
      break;

    case "delete":
      displayDeleteConfirmation();
      break;
  }
};
</script>

<template>
  <FlashMessage
    v-if="flashMessage.isActive.value"
    :state="flashMessage.state.value"
  >
    {{ flashMessage.message.value }}
    <router-link
      v-if="flashMessage.showRouter"
      class="link"
      :to="{
        name: 'DocumentSpace',
        params: { id: flashMessage.spaceId.value },
      }"
    >
      {{ $t("customFlows.shareModal.addedToSpace.goToSpace") }}
    </router-link>
  </FlashMessage>

  <div class="pane page_content flow_container">
    <div
      v-if="flowFetching"
      class="loading_wrapper"
      data-test="custom-flow-loading"
    >
      <div class="loading_status">
        <LoadingSpinner :color="'var(--classic-text, black)'" />
      </div>
    </div>

    <div
      v-else-if="!flow"
      class="empty_pane_container"
      data-test="custom-flow-missing"
    >
      <span class="material-symbols-outlined"> error_outline </span>
      <span class="text">
        {{ $t("customFlows.detail.doesNotExist") }}
      </span>
    </div>

    <div v-else class="flow_content_wrapper">
      <div class="title_container">
        <Breadcrumbs :items="breadcrumbItems" class="breadcrumbs" />

        <div class="control_container">
          <Dropdown :items="actionItems" @change="handleAction">
            <div>
              <span class="material-symbols-outlined">more_vert</span>
            </div>
          </Dropdown>

          <button
            :class="{
              button: true,
              primary: true,
              disabled: pages?.length === 0,
            }"
            :disabled="!pages?.length"
            @click="viewPageInPresenter(1)"
            data-test="present-flow-ctrl"
          >
            <span class="material-symbols-outlined">play_arrow</span>
            {{ $t("customFlows.detail.present") }}
          </button>
          <button
            v-if="canUseSharedSpaces"
            :class="{
              button: true,
              primary: true,
              disabled: pages?.length === 0,
            }"
            :disabled="!pages?.length"
            @click="openShareModal"
            data-test="share-flow-ctrl"
          >
            <span class="material-symbols-outlined">share</span>
            {{ $t("customFlows.detail.share") }}
          </button>
        </div>
      </div>

      <div
        v-if="canUseSharedSpaces && isFlowShared"
        class="shared_indicator"
        data-test="custom-flow-shared-indicator"
      >
        <span class="material-symbols-outlined">error</span>
        <span>{{ t("customFlows.detail.sharedIndicator") }}</span>
      </div>

      <div v-if="pages && pages?.length > 0">
        <draggable-grid
          class="columns grid_drag flow_gallery"
          @placeAbove="placeBefore"
          @placeBelow="placeBehind"
        >
          <template v-for="(page, idx) in pages" :key="page!.id">
            <CustomFlowPage
              v-if="page"
              :pageId="page!.pageId!"
              :videoId="page!.video?.id"
              :videoThumbnail="page!.video?.thumbnailDownloadUrl"
              :id="page!.id"
              :index="idx + 1"
              :document-title="getDocumentTitle(page as FlowItem) ?? ''"
              :is-expired="
                page?.page?.document?.documentExpiration?.expiredAt
                  ? true
                  : false
              "
              @delete="handleRemoveFlowPage"
              @present="viewPageInPresenter"
              @dblclick="viewPageInPresenter(idx + 1)"
              :data-idx="idx"
              :data-id="page!.id"
              :data-test="`custom-flow-page-${page!.page?.pageNumber}`"
            />
          </template>

          <div class="add_pages_container" :data-ignored="true">
            <button
              class="item_button"
              data-test="add-page-ctrl"
              @click="openDocumentPagePicker"
            >
              <span class="material-symbols-outlined"> note_add </span>
              <label>{{ $t("customFlows.detail.addPagesLabel") }}</label>
            </button>
          </div>
        </draggable-grid>
      </div>

      <div v-else class="add_pages_container">
        <button
          class="item_button"
          @click="openDocumentPagePicker"
          data-test="add-page-ctrl"
        >
          <span class="material-symbols-outlined"> note_add </span>
          <label>{{ $t("customFlows.detail.addPagesLabel") }}</label>
        </button>
      </div>

      <div
        v-if="pages && pages.length && hasLegalAppendixPages"
        class="legal_appendix_container"
      >
        <div class="divide">
          <hr />
        </div>

        <div ref="legalAppendixContainerElem" class="legal_appendix_gallery">
          <div
            v-for="page in legalAppendixPages"
            :key="page?.id"
            class="legal_appendices"
          >
            <CustomFlowPage
              v-if="page"
              :page-id="page!.id"
              :id="page!.id"
              :index="0"
              :is-appendix="true"
              :document-title="page!.document?.displayTitle ?? ''"
              :is-expired="
                page?.document?.documentExpiration?.expiredAt ? true : false
              "
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <DocumentAndPagesPicker
    v-if="isDocumentPagePickerActive"
    :pending="appendingPages"
    @close="closeDocumentPagePicker"
    @submit="appendFlowPages"
    @addVideo="addVideo"
  />

  <delete-custom-flow-modal
    v-if="showDeleteModal"
    :active="true"
    :flowId="flowId"
    :title="flow!.title"
    @close="showDeleteModal = false"
    @success="deleteFlow"
  />

  <rename-custom-flow-modal
    v-if="isRenameModalActive"
    :active="true"
    :flowId="flowId"
    :previous-title="flow?.title!"
    @close="closeRenameModal"
    @success="handleFlowRenamed"
    @error="
      flashMessage.show(
        $t('customFlows.flashMessages.renamingFlow.error'),
        'error',
      )
    "
  />

  <duplicate-custom-flow-modal
    v-if="isDuplicateModalActive"
    :active="true"
    :flow-id="flowId"
    :flow-title="flow?.title!"
    @close="closeDuplicateModal"
    @success="handleFlowDuplicated"
    @error="handleDuplicateError"
  />

  <delete-flow-page-modal
    v-if="showRemovePageModal"
    :active="true"
    :pageId="flowPageIdToBeRemoved!"
    @close="closeRemovePageModal"
    @pageRemoved="handlePageDeleted"
    @error="handleErrorDeletingPage"
  />

  <share-custom-flow-modal
    v-if="canUseSharedSpaces && isShareModalActive"
    :active="true"
    :inherit-classic-theme="true"
    :flowId="flowId"
    @close="closeShareModal"
    @success="handleShareFlow"
  />
</template>

<style lang="scss" scoped>
$pageWidth: 335px;

.pane {
  height: 100%;

  .loading_wrapper {
    height: 100%;

    .loading_status {
      display: flex;
      align-items: center;
      justify-content: center;
      height: calc(100% - 75px);
    }
  }

  &.empty_pane_container {
    height: 75%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .material-symbols-outlined {
      font-size: 6em;
      opacity: 0.4;
    }

    .text {
      margin: 0.6em 0 1.5em 0;
    }
  }

  &.flow_container {
    display: flex;
    flex-direction: column;

    .flow_content_wrapper {
      display: flex;
      flex-direction: column;
      width: 100%;
      justify-content: center;

      .title_container {
        display: flex;
        margin-bottom: 2em;

        .breadcrumbs {
          font-size: 1.1rem;
          font-weight: 500;
          flex: 1;
          min-width: 0;
          overflow-wrap: anywhere;
        }

        .control_container {
          flex-shrink: 0;
          display: flex;
          align-items: center;

          .button {
            margin-left: 0.5em;
          }
        }
      }

      .shared_indicator {
        display: inline-flex;
        align-self: flex-start;
        align-items: center;
        gap: 0.4em;
        margin-top: -1em;
        margin-bottom: 1.5em;
        padding: 0.6em 1.2em;
        border-radius: 999px;
        background-color: var(
          --classic-accent-soft,
          rgba($color-primary, 0.12)
        );
        color: var(--classic-accent, $color-primary);
        font-size: 0.9rem;
        font-weight: 500;

        .material-symbols-outlined {
          font-size: 1.2rem;
        }
      }

      .flow_gallery {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        gap: 20px;
      }

      .add_pages_container {
        .item_button {
          width: $pageWidth;
          height: $pageWidth;
          border-radius: 0.35em;
          border: 1px solid transparent;
          background-color: transparent;
          display: flex;
          align-self: flex-start;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 0.5em;

          &:hover {
            border: 1px solid var(--classic-border, #dbdbdb);
            cursor: pointer;
          }

          .material-symbols-outlined {
            font-size: 4em;
            opacity: 0.7;
          }

          label {
            font-size: medium;
          }
        }
      }

      .divide {
        width: 100%;
        margin: 2em 0;
        hr {
          border: 1px solid var(--classic-border, #dbdbdb);
          border-radius: 1px;
        }
      }

      .flow_gallery :deep(.dropItem) {
        box-sizing: border-box;
        border: 2px dashed var(--classic-accent, #35b6ba);
        border-radius: 0.35em;
        background-color: var(--classic-accent-soft, rgba(#35b6ba, 0.12));
      }

      .legal_appendix_container {
        display: flex;
        flex-direction: column;

        .legal_appendix_gallery {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-start;
          gap: 20px;
        }
      }
    }
  }
}
</style>
